import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent} from 'react';

import {BLUE} from 'ui-library/Colors';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import SortableTable, {SortableTableColumn} from 'ui-library/SortableTable';
import {ElementMouseEvent} from 'util/Types';

export interface CheckableSortableTableRow {
  id: string | number;
  checked: boolean;
  disabled?: boolean;
}

/** @package */
export interface CheckableSortableTableProps {
  columns: Array<SortableTableColumn>;
  rows: Array<CheckableSortableTableRow>;
  onChangeChecks?: (newCheckedRowIds: Array<string | number>) => void;
  disableCheckboxes: boolean;
  className?: string;
  onClickRow?: (row: CheckableSortableTableRow, e?: ElementMouseEvent) => void;
  emptyTableText?: string;
  clickable?: boolean;
  selectable?: boolean;
  hoverable?: boolean;
  coloredHeader?: boolean;
  verticalAlignMiddle?: boolean;
  wrapCell?: boolean;
}

/**
 * TODO(BNCH-13149) CheckableSortableTable is outdated, but we don't have a suitable replacement for it so cannot
 * mark it as legacy yet. We should either write a replacement or update the implementation and behavior.
 */
function CheckableSortableTable(props: CheckableSortableTableProps): JSX.Element {
  const getCheckedRowIds = (): Array<string | number> =>
    props.rows.filter(({checked}) => checked).map(({id}) => id);

  const handleCheckRow = (rowId: string | number, e: ChangeEvent<HTMLInputElement>): void => {
    const newCheckedRowIds: Array<string | number> = e.target.checked
      ? [...getCheckedRowIds(), rowId]
      : getCheckedRowIds().filter((rid) => rid !== rowId);

    if (typeof props.onChangeChecks === 'function') {
      props.onChangeChecks(newCheckedRowIds);
    }
  };

  const getRowsWithCheckboxes = (): Array<{checked: JSX.Element; id: string | number}> =>
    props.rows.map((row) => {
      const checkbox = (
        <input
          type="checkbox"
          className={css(styles.checkbox)}
          checked={row.checked}
          disabled={props.onChangeChecks == null || props.disableCheckboxes || row.disabled}
          onChange={(e) => {
            handleCheckRow(row.id, e);
          }}
        />
      );
      return {
        ...row,
        checked: checkbox,
      };
    });

  const handleAllCheckboxChange = (e: ChangeEvent<HTMLInputElement>): void => {
    let newCheckedRowIds: Array<string | number>;
    const disabledCheckedRowIds = props.rows
      .filter(({disabled, checked}) => disabled && checked)
      .map(({id}) => id);
    const disabledUncheckedRowIds = props.rows
      .filter(({disabled, checked}) => disabled && !checked)
      .map(({id}) => id);
    if (e.target.checked) {
      newCheckedRowIds = props.rows
        .map(({id}) => id)
        // don't check currently unchecked disabled rows
        .filter((id) => !disabledUncheckedRowIds.includes(id));
    } else {
      // keep disabled checked rows checked
      newCheckedRowIds = disabledCheckedRowIds;
    }
    if (typeof props.onChangeChecks === 'function') {
      props.onChangeChecks(newCheckedRowIds);
    }
  };

  const allCheckbox = (
    <IndeterminateCheckbox
      items={props.rows.map(({checked}) => checked)}
      selectedMap={(checked) => checked}
      disabled={props.onChangeChecks == null || props.disableCheckboxes}
      onChange={handleAllCheckboxChange}
    />
  );
  const checkboxColumn = SortableTable.defaultColumn(allCheckbox, 'checked', {sortable: false});
  return (
    <SortableTable
      {...props}
      columns={[checkboxColumn, ...props.columns]}
      rows={getRowsWithCheckboxes()}
      emptyTableText={props.emptyTableText}
      dataTestAttrs={{component: 'CheckableSortableTable', element: 'root'}}
    />
  );
}

CheckableSortableTable.defaultProps = {disableCheckboxes: false};

const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default CheckableSortableTable;
